(() => {
  const name = 'happysoy-benefit-rotator';
  if (customElements.get(name)) return;

  class HappysoyBenefitRotator extends HTMLElement {
    connectedCallback() {
      const connection = (this._connection || 0) + 1;
      this._connection = connection;
      // A section can be parsed after this element has already been registered.
      queueMicrotask(() => {
        if (this.isConnected && this._connection === connection) this._initialize();
      });
    }

    disconnectedCallback() {
      this._connection = (this._connection || 0) + 1;
      this._cleanup();
      delete this.dataset.ready;
    }

    _initialize() {
      this._cleanup();
      this._window = this.ownerDocument.defaultView;
      this._slides = Array.from(this.querySelectorAll('.hs-benefit-rotator__slide'));
      this._viewport = this.querySelector('.hs-benefit-rotator__viewport');
      this._toggle = this.querySelector('[data-benefit-toggle]');
      this._next = this.querySelector('[data-benefit-next]');
      this._status = this.querySelector('[data-benefit-status]');
      this.dataset.count = String(this._slides.length);
      if (!this._slides.length || !this._viewport) return;

      this._listeners = [];
      this._contacts = new Map();
      this._gesture = null;
      this._hovered = false;
      this._focused = false;
      this._autoplay = this.dataset.autoplay !== 'false';
      this._userPaused = this._userPaused === true;
      const interval = Number(this.dataset.interval);
      this._interval = Number.isFinite(interval) && interval >= 3 && interval <= 60 ? interval * 1000 : 6000;
      this._index = Math.max(0, this._slides.findIndex(slide => slide.dataset.active === 'true'));
      this._motion = this._window.matchMedia?.('(prefers-reduced-motion: reduce)');
      this._reduced = this._motion?.matches === true;
      this._visible = !this._window.IntersectionObserver;
      this._listen(this, 'mouseenter', () => { this._hovered = true; this._schedule(); });
      this._listen(this, 'mouseleave', () => { this._hovered = false; this._schedule(); });
      this._listen(this, 'focusin', event => {
        this._focused = !this._toggle?.contains(event.target);
        this._schedule();
      });
      this._listen(this, 'focusout', event => {
        this._focused = !!event.relatedTarget && this.contains(event.relatedTarget) && !this._toggle?.contains(event.relatedTarget);
        this._schedule();
      });
      this._listen(this.ownerDocument, 'visibilitychange', () => this._schedule());
      this._listen(this._next, 'click', event => this._go(this._index + 1, true, event.detail === 0));
      this._listen(this._toggle, 'click', () => {
        if (!this._autoplay || this._reduced) return;
        this._userPaused = !this._userPaused;
        this._renderToggle();
        this._schedule();
      });
      this._listen(this._viewport, 'keydown', event => {
        const indices = { ArrowLeft: this._index - 1, ArrowRight: this._index + 1, Home: 0, End: this._slides.length - 1 };
        if (!(event.key in indices) || event.altKey || event.ctrlKey || event.metaKey) return;
        event.preventDefault();
        this._go(indices[event.key], true, true);
      });
      this._listen(this._window, 'pointerdown', event => this._pointerDown(event), { passive: true });
      this._listen(this._window, 'pointerup', event => this._pointerUp(event), { passive: true });
      this._listen(this._window, 'pointercancel', event => {
        this._contacts.delete(event.pointerId);
        this._gesture = null;
        this._schedule();
      }, { passive: true });

      const motionChange = event => {
        this._reduced = event.matches;
        this.dataset.reducedMotion = String(this._reduced);
        this._renderToggle();
        this._schedule();
      };
      if (this._motion?.addEventListener) this._listen(this._motion, 'change', motionChange);
      else if (this._motion?.addListener) {
        this._motion.addListener(motionChange);
        this._listeners.push(() => this._motion.removeListener(motionChange));
      }
      if (this._window.IntersectionObserver) {
        const observer = new this._window.IntersectionObserver(entries => {
          const entry = entries.find(item => item.target === this);
          if (!entry || !this.isConnected || this._observer !== observer) return;
          this._visible = entry.isIntersecting && entry.intersectionRatio > 0;
          this._schedule();
        });
        this._observer = observer;
        observer.observe(this);
      }
      this._viewport.tabIndex = this._slides.length > 1 ? 0 : -1;
      this.dataset.reducedMotion = String(this._reduced);
      this._render();
      this._renderToggle();
      this.dataset.ready = 'true';
      this._schedule();
    }

    _listen(target, event, listener, options) {
      if (!target) return;
      target.addEventListener(event, listener, options);
      this._listeners.push(() => target.removeEventListener(event, listener, options));
    }

    _cleanup() {
      this._clearTimer();
      this._observer?.disconnect();
      this._observer = null;
      this._listeners?.forEach(remove => remove());
      this._listeners = [];
      this._contacts?.clear();
      this._gesture = null;
    }

    _clearTimer() {
      if (this._timer !== undefined) this._window?.clearTimeout(this._timer);
      this._timer = undefined;
    }

    _schedule() {
      this._clearTimer();
      if (!this.isConnected || this._slides.length < 2 || !this._autoplay || this._userPaused || this._reduced ||
          !this._visible || this.ownerDocument.hidden || this._hovered || this._focused || this._contacts.size) return;
      this._timer = this._window.setTimeout(() => {
        this._timer = undefined;
        this._go(this._index + 1, false);
      }, this._interval);
    }

    _go(index, manual, instant = false) {
      if (this._slides.length < 2) return;
      this.dataset.instant = String(instant);
      this._index = (index + this._slides.length) % this._slides.length;
      this._render();
      if (manual && this._status) {
        const template = this.dataset.statusTemplate || '{{ current }} von {{ total }}';
        const position = template.replace(/\{\{\s*current\s*\}\}|%current%/g, String(this._index + 1))
          .replace(/\{\{\s*total\s*\}\}|%total%/g, String(this._slides.length));
        const content = this._slides[this._index].textContent.trim().replace(/\s+/g, ' ');
        this._status.textContent = `${position}: ${content}`;
      }
      this._schedule();
    }

    _render() {
      this._slides.forEach((slide, index) => {
        const active = index === this._index;
        slide.dataset.active = String(active);
        slide.setAttribute('aria-hidden', String(!active));
      });
    }

    _renderToggle() {
      if (!this._toggle) return;
      this._toggle.hidden = this._slides.length < 2 || !this._autoplay || this._reduced;
      this._toggle.setAttribute('aria-label', this._userPaused ? this.dataset.playLabel || 'Vorteile automatisch wechseln' : this.dataset.pauseLabel || 'Automatischen Wechsel pausieren');
      const pauseIcon = this._toggle.querySelector('[data-icon-pause]');
      const playIcon = this._toggle.querySelector('[data-icon-play]');
      if (pauseIcon) pauseIcon.hidden = this._userPaused;
      if (playIcon) playIcon.hidden = !this._userPaused;
      this.dataset.paused = String(this._userPaused);
    }

    _pointerDown(event) {
      if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
      this._contacts.set(event.pointerId, true);
      this._schedule();
      if (this._contacts.size !== 1 || !this._viewport.contains(event.target)) { this._gesture = null; return; }
      this._gesture = { id: event.pointerId, x: event.clientX, y: event.clientY };
    }

    _pointerUp(event) {
      if (!this._contacts.has(event.pointerId)) return;
      const gesture = this._gesture;
      this._contacts.delete(event.pointerId);
      this._gesture = null;
      this._schedule();
      if (!gesture || gesture.id !== event.pointerId || this._contacts.size) return;
      const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
      if (Math.abs(dx) < 30 || Math.abs(dx) <= Math.abs(dy) * 1.25) return;
      this._go(this._index + (dx < 0 ? 1 : -1), true);
    }
  }

  customElements.define(name, HappysoyBenefitRotator);
})();
